/**
 * Dashboard /settings tab — language, currency, timezone, linked accounts,
 * passkeys, notifications prefs, Danger Zone (delete account).
 *
 * Originally lines 1173-1282 of `routes/dashboard.ts`. Phase 2.2.
 */
import { Auth } from '../../auth';
import { escapeHtml } from '../../utils';
import { t } from '../../i18n';
import { apiTokensSection } from './api-tokens';
import { confirmChannelSection } from './confirm-channel';

export function settingsPanel(): string {
  const u = Auth.getUser();
  const email = u?.email ?? '';
  return `
<h2 class="dash-h2">${escapeHtml(t('dash.settings.title'))}</h2>
<div class="dash-settings">
  <section class="dash-settings__group">
    <h3>${escapeHtml(t('dash.settings.groupAccount'))}</h3>
    <dl class="dash-kv">
      <dt data-i18n="dash.settings.fieldDisplayName">${escapeHtml(t('dash.settings.fieldDisplayName'))}</dt><dd>${escapeHtml(u?.name ?? '—')}</dd>
    </dl>
    <form class="dash-settings__email-form" data-email-form>
      <label class="dash-settings__email-label" for="settings-email" data-i18n="dash.settings.fieldEmail">${escapeHtml(t('dash.settings.fieldEmail'))}</label>
      <div class="dash-settings__email-row">
        <input
          type="email"
          id="settings-email"
          name="email"
          maxlength="254"
          autocomplete="email"
          class="dash-settings__email-input"
          value="${escapeHtml(email)}"
          placeholder="${escapeHtml(t('dash.settings.emailPlaceholder'))}"
          data-email-input
        />
        <button type="submit" class="btn btn--accent btn--sm" data-email-save data-i18n="dash.settings.emailSave">${escapeHtml(t('dash.settings.emailSave'))}</button>
      </div>
      <p class="dash-sub dash-settings__email-hint" data-i18n="dash.settings.emailHint">${escapeHtml(t('dash.settings.emailHint'))}</p>
    </form>
  </section>
  <section class="dash-settings__group" data-link-accounts-section>
    <h3>${escapeHtml(t('dash.settings.linkedAccountsTitle'))}</h3>
    <p class="dash-sub" style="margin-bottom: 12px;" data-i18n="dash.settings.linkedAccountsIntro">${escapeHtml(t('dash.settings.linkedAccountsIntro'))}</p>
    <p class="dash-sub" style="margin-bottom: 12px; font-style: italic; display:none;" data-link-origin-hint></p>
    <div class="dash-pref" data-link-row="google">
      <span>
        <strong style="display:block;" data-i18n="dash.settings.providerGoogle">${escapeHtml(t('dash.settings.providerGoogle'))}</strong>
        <span class="dash-sub" data-link-status="google" data-i18n="auth.verifying">${escapeHtml(t('auth.verifying'))}</span>
        <span class="dash-sub" data-link-identifier="google" style="display:none; font-size:12px; color:var(--ink-3);"></span>
      </span>
      <button class="btn btn--ghost btn--sm" data-link-add="google" style="display:none;" data-i18n="dash.settings.addProviderButton">${escapeHtml(t('dash.settings.addProviderButton'))}</button>
    </div>
    <div class="dash-pref" data-link-row="telegram">
      <span>
        <strong style="display:block;" data-i18n="dash.settings.providerTelegram">${escapeHtml(t('dash.settings.providerTelegram'))}</strong>
        <span class="dash-sub" data-link-status="telegram" data-i18n="auth.verifying">${escapeHtml(t('auth.verifying'))}</span>
        <span class="dash-sub" data-link-identifier="telegram" style="display:none; font-size:12px; color:var(--ink-3);"></span>
      </span>
      <button class="btn btn--ghost btn--sm" data-link-add="telegram" style="display:none;" data-i18n="dash.settings.addProviderButton">${escapeHtml(t('dash.settings.addProviderButton'))}</button>
    </div>
    <div data-link-mount-google style="display:none; margin-top: 16px;"></div>
    <div data-link-mount-telegram style="display:none; margin-top: 16px;"></div>
  </section>
  <section class="dash-settings__group" data-push-section hidden>
    <h3>${escapeHtml(t('dash.settings.groupPush'))}</h3>
    <p class="dash-sub" style="margin-bottom: 12px;" data-i18n="dash.settings.pushIntro">${escapeHtml(t('dash.settings.pushIntro'))}</p>
    <div class="dash-pref">
      <span>
        <strong style="display:block;" data-i18n="dash.settings.pushEnable">${escapeHtml(t('dash.settings.pushEnable'))}</strong>
        <span class="dash-sub" data-push-status data-i18n="dash.settings.pushStatusChecking">${escapeHtml(t('dash.settings.pushStatusChecking'))}</span>
      </span>
      <button class="btn btn--ghost btn--sm" data-push-toggle disabled data-i18n="dash.settings.pushBtnEnable">${escapeHtml(t('dash.settings.pushBtnEnable'))}</button>
    </div>
    <div data-push-categories hidden>
      <div class="dash-pref">
        <span data-i18n="dash.settings.pushLowBalance">${escapeHtml(t('dash.settings.pushLowBalance'))}</span>
        <label class="dash-toggle"><input type="checkbox" data-push-pref="lowBalance" /><span></span></label>
      </div>
      <div class="dash-pref">
        <span data-i18n="dash.settings.pushPaymentPaid">${escapeHtml(t('dash.settings.pushPaymentPaid'))}</span>
        <label class="dash-toggle"><input type="checkbox" data-push-pref="paymentPaid" /><span></span></label>
      </div>
      <div class="dash-pref">
        <span data-i18n="dash.settings.pushEsimActivated">${escapeHtml(t('dash.settings.pushEsimActivated'))}</span>
        <label class="dash-toggle"><input type="checkbox" data-push-pref="esimActivated" /><span></span></label>
      </div>
      <div class="dash-pref">
        <span data-i18n="dash.settings.pushCountryWelcome">${escapeHtml(t('dash.settings.pushCountryWelcome'))}</span>
        <label class="dash-toggle"><input type="checkbox" data-push-pref="countryWelcome" /><span></span></label>
      </div>
      <div class="dash-pref">
        <span data-i18n="dash.settings.pushInactivePing">${escapeHtml(t('dash.settings.pushInactivePing'))}</span>
        <label class="dash-toggle"><input type="checkbox" data-push-pref="inactivePing" /><span></span></label>
      </div>
    </div>
  </section>
  <section class="dash-settings__group" data-passkey-section hidden>
    <h3>${escapeHtml(t('dash.settings.passkeysTitle'))}</h3>
    <p class="dash-sub" style="margin-bottom: 12px;" data-i18n="dash.settings.passkeysIntro">${escapeHtml(t('dash.settings.passkeysIntro'))}</p>
    <div data-passkey-list></div>
    <div class="dash-settings__passkey-actions">
      <button class="btn btn--ghost btn--sm" data-passkey-add data-i18n="dash.settings.passkeysAddButton">${escapeHtml(t('dash.settings.passkeysAddButton'))}</button>
      <span class="dash-sub" data-passkey-status></span>
    </div>
  </section>
  <section class="dash-settings__group">
    <h3>${escapeHtml(t('dash.settings.groupPreferences'))}</h3>
    <div class="dash-pref"><span data-i18n="dash.settings.prefLanguage">${escapeHtml(t('dash.settings.prefLanguage'))}</span><button class="btn btn--ghost btn--sm" data-action="open-lang">English ▾</button></div>
    <div class="dash-pref"><span data-i18n="dash.settings.prefCurrency">${escapeHtml(t('dash.settings.prefCurrency'))}</span><span class="mono" data-i18n="dash.settings.prefCurrencyValue">${escapeHtml(t('dash.settings.prefCurrencyValue'))}</span></div>
    <div class="dash-pref"><span data-i18n="dash.settings.prefTimezone">${escapeHtml(t('dash.settings.prefTimezone'))}</span><span class="mono" data-i18n="dash.settings.prefTimezoneAuto">${escapeHtml(t('dash.settings.prefTimezoneAuto'))}</span></div>
  </section>
  ${apiTokensSection()}
  ${confirmChannelSection()}
  <section class="dash-settings__group dash-settings__danger">
    <h3>${escapeHtml(t('dash.settings.groupDanger'))}</h3>
    <button class="btn btn--ghost" data-action="logout" data-i18n="dash.settings.signOut">${escapeHtml(t('dash.settings.signOut'))}</button>
    <button class="btn btn--ghost dash-danger-btn" data-action="delete-account-request" data-user-id="${escapeHtml(u?.id ?? '')}" type="button" data-i18n="dash.settings.deleteAccount">${escapeHtml(t('dash.settings.deleteAccount'))}</button>
    <p class="dash-sub" data-i18n="dash.settings.deleteWarning">${escapeHtml(t('dash.settings.deleteWarning'))}</p>
  </section>
</div>`;
}

